import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {Platform} from '../../classes/platform';
import {PlatformService} from '../../services/platform.service';
import {CheckpointService} from '../../services/checkpoint.service';
import {Checkpoint, level} from '../../classes/checkpoint';
import {MatIconButton} from '@angular/material/button';
import {CheckpointLevelStatusPipe} from '../../pipes/checkpoint-level-status.pipe';
import {DatePipe} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {Routenames} from '../../classes/route-name.enum';
import {Server} from '../../classes/server';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {UrlPrefixStrippingPipe} from '../../pipes/url-prefix-stripping.pipe';
import {LastCheckReadablePipe} from '../../pipes/last-check-readable.pipe';
import {SslValidUntilPipe} from '../../pipes/ssl-valid-until.pipe';
import {DaysFromNowPipe} from '../../pipes/days-from-now.pipe';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs';

const LEVEL_PRIORITY: Record<string, number> = {
    [level.PRODUCTION]: 1,
    [level.HEALTH]: 2,
    [level.CHECK]: 3,
    [level.ACCEPTANCE]: 4,
};

@Component({
    selector: 'app-platform-checkpoints',
    templateUrl: './platform-checkpoints.component.html',
    styleUrls: ['./platform-checkpoints.component.scss'],
    imports: [
        RouterLink,
        CheckpointLevelStatusPipe,
        DatePipe,
        MatIcon,
        MatIconButton,
        BreadcrumbComponent,
        HasFeaturePipe,
        UrlPrefixStrippingPipe,
        LastCheckReadablePipe,
        SslValidUntilPipe,
        DaysFromNowPipe
    ]
})
export class PlatformCheckpointsComponent extends CodaltComponent implements OnInit, OnDestroy {

    readonly platform = signal<Platform>(new Platform());
    readonly platforms = signal<Platform[]>([]);
    readonly servers = signal<{ name: string, server: Server }[]>([]);
    readonly checkpoints = signal<Checkpoint[]>(null);
    readonly isAll = signal(false);

    // Subscriptions of the current route params, replaced when navigating to another platform
    private routeSubscriptions = new Subscription();

    readonly sortedCheckpoints = computed(() => {
        const list = this.checkpoints();
        if (!list?.length) {
            return list;
        }
        return [...list].sort((a, b) => {
            const levelDiff = (LEVEL_PRIORITY[a.level] ?? 99) - (LEVEL_PRIORITY[b.level] ?? 99);
            if (levelDiff !== 0) {
                return levelDiff;
            }
            return this.getStatusPriority(a) - this.getStatusPriority(b);
        });
    });

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointService: CheckpointService,
        private platformService: PlatformService,
        private titleService: Title,
    ) {
        super();
    }

    ngOnInit() {
        let extraParam = null;
        this.subscriptions.add(
            this.activatedRoute.queryParams.subscribe((params: { level }) => {
                extraParam = params.level;
            })
        );

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { platformId }) => {
                const isAll = params.platformId === 'all';
                this.isAll.set(isAll);
                this.routeSubscriptions.unsubscribe();
                this.routeSubscriptions = new Subscription();
                this.subscriptions.add(this.routeSubscriptions);
                this.checkpoints.set(null);

                const platformId = isAll ? null : +params.platformId;

                this.routeSubscriptions.add(
                    this.checkpointService.getCheckpointsLive(platformId).subscribe({
                        next: checkpoints => {
                            this.checkpoints.set(extraParam === undefined ? checkpoints : checkpoints.filter(checkpoint => checkpoint.level === extraParam));
                        },
                        error: err => console.error(err)
                    })
                );

                if (isAll) {
                    this.crumbs = [
                        {name: 'Seire', route: Routenames.seire},
                        {name: 'Alle checkpoints', route: `${Routenames.seire}/all`}
                    ];
                    this.titleService.setTitle(`Alle checkpoints - Seire ${this.environment.titleAppend}`);

                    this.routeSubscriptions.add(
                        this.platformService.getPlatformsLive().subscribe({
                            next: platforms => this.platforms.set(platforms),
                            error: err => console.error(err)
                        })
                    );
                    return;
                }

                this.routeSubscriptions.add(
                    this.platformService.liveItem(platformId, this.platformService.getPlatform(platformId)).subscribe({
                        next: platform => this.setPlatform(platform),
                        error: err => console.error(err)
                    })
                );
            })
        );
    }

    private setPlatform(platform: Platform) {
        this.platform.set(platform);

        this.servers.set([
            {name: 'Production', server: platform.prod_server},
            {name: 'Acceptance', server: platform.acc_server},
            {name: 'Test', server: platform.test_server}
        ].filter(p => p.server !== null));

        this.crumbs = [
            {name: 'Seire', route: Routenames.seire},
            {name: `Platform : ${this.platform().name}`, route: `${Routenames.seire}/${this.platform().id}`}
        ];
        this.titleService.setTitle(`${this.platform().name} - Seire ${this.environment.titleAppend}`);
    }

    platformName(platformId: number): string {
        return this.platforms().find(platform => +platform.id === +platformId)?.name ?? '';
    }

    getStatusPriority(checkpoint: Checkpoint): number {
        if (checkpoint.checkpoint_results_count === 0) {
            return 5; // inactive
        }

        if (checkpoint.postponed_till && new Date(checkpoint.postponed_till) > new Date()) {
            return 3; // postponed
        }

        const lastInstance = checkpoint.checkpoint_result_last_instance ?? null;
        const formattedLastInstance = lastInstance ? new Date(lastInstance.created_at) : null;
        const timeBehind = new Date();
        timeBehind.setDate(timeBehind.getDate() - 1);

        if (checkpoint.error || (lastInstance !== null && formattedLastInstance < timeBehind)) {
            return 1; // error
        }

        if (checkpoint.warning) {
            return 2; // warning
        }

        return 4; // ok / success
    }

    readonly isOlderThan2Days = (lastUpdate: Date | string) => {
        const last = new Date(lastUpdate);
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
        return last < twoDaysAgo;
    };
}
